import { AnswerSurgicalPendency } from 'domain/usecases/surgical-pendency/answer-surgical-pendency'
import { LoadSurgicalPendency } from 'domain/usecases/surgical-pendency/load-surgical-pendency'
import { LoadSurgicalPendencyHistory } from 'domain/usecases/surgical-pendency/load-surgical-pendency-history'
import { LoadSurgicalOrderTimeLine } from 'domain/usecases/surgical-pendency/load-surgical-order-time-line'
import { UploadSurgicalPendencyDocument } from 'domain/usecases/surgical-pendency/upload-surgical-pendency-document'
import { LoadSurgicalPendencyDocument } from 'domain/usecases/surgical-pendency/load-surgical-pendency-document'
import { makeAutoObservable } from 'mobx'
import { LoadSurgicalOrderNoticeCard } from 'domain/usecases/surgical-pendency/load-surgical-order-notice-card'
import { AddSurgicalPendency } from 'domain/usecases/surgical-pendency/add-surgical-pendency'
import { FinishSurgicalPendency } from 'domain/usecases/surgical-pendency/finish-surgical-pendency'
import { LoadHelpPendencyHistory } from 'domain/usecases/surgical-pendency/load-help-pendency-history'
import { CreateHelpPendencyMessage } from 'domain/usecases/surgical-pendency/create-help-pendency-message'
import { FinishHelpPendency } from 'domain/usecases/surgical-pendency/finish-help-pendency'
import { LoadAuthorizedCommunicationHistory } from 'domain/usecases/surgical-pendency/load-authorized-communication-history'
import { CreateAuthorizedCommunicationMessage } from 'domain/usecases/surgical-pendency/create-authorized-communication-message'
import { FinishAuthorizedCommunication } from 'domain/usecases/surgical-pendency/finish-authorized-communication'
import { AnswerDoctorCommunication } from 'domain/usecases/surgical-pendency/answer-doctor-communication'

type NewType = {
  answerSurgicalPendency: AnswerSurgicalPendency
  uploadSurgicalPendencyDocument: UploadSurgicalPendencyDocument
  loadSurgicalPendencyDocument: LoadSurgicalPendencyDocument
  loadSurgicalPendency: LoadSurgicalPendency
  loadSurgicalOrderTimeLine: LoadSurgicalOrderTimeLine
  loadSurgicalPendencyHistory: LoadSurgicalPendencyHistory
  loadSurgicalOrderNoticeCard: LoadSurgicalOrderNoticeCard
  addSurgicalPendency: AddSurgicalPendency
  finishSurgicalPendency: FinishSurgicalPendency
  loadHelpPendencyHistory: LoadHelpPendencyHistory
  createHelpPendencyMessage: CreateHelpPendencyMessage
  finishHelpPendency: FinishHelpPendency
  loadAuthorizedCommunicationHistory: LoadAuthorizedCommunicationHistory
  createAuthorizedCommunicationMessage: CreateAuthorizedCommunicationMessage
  finishAuthorizedCommunication: FinishAuthorizedCommunication
  answerDoctorCommunication: AnswerDoctorCommunication
}

type Services = NewType

export class SurgicalPendencyService {
  constructor(private readonly services: Services) {
    makeAutoObservable(this)
  }

  async answerSurgicalPendency(
    params: AnswerSurgicalPendency.Params
  ): Promise<AnswerSurgicalPendency.Model> {
    return this.services.answerSurgicalPendency.answer(params)
  }

  async uploadSurgicalPendencyDocument(
    params: UploadSurgicalPendencyDocument.Params
  ): Promise<UploadSurgicalPendencyDocument.Model> {
    return this.services.uploadSurgicalPendencyDocument.upload(params)
  }

  async loadSurgicalPendencyDocument(
    surgical_order_id: number,
    document_id: number,
    group_id?: string
  ): Promise<LoadSurgicalPendencyDocument.Model> {
    return this.services.loadSurgicalPendencyDocument.load(
      surgical_order_id,
      document_id,
      group_id
    )
  }

  async loadSurgicalPendency(
    surgical_order_id: number
  ): Promise<LoadSurgicalPendency.Model> {
    return this.services.loadSurgicalPendency.load(surgical_order_id)
  }

  async loadSurgicalOrderTimeLine(
    surgical_order_id: number
  ): Promise<LoadSurgicalOrderTimeLine.Model> {
    return this.services.loadSurgicalOrderTimeLine.load(surgical_order_id)
  }

  async loadSurgicalPendencyHistory(
    surgical_pendency_id: number
  ): Promise<LoadSurgicalPendencyHistory.Model> {
    return this.services.loadSurgicalPendencyHistory.load(surgical_pendency_id)
  }

  async loadHelpPendencyHistory(
    surgical_pendency_id: number
  ): Promise<LoadHelpPendencyHistory.Model> {
    return this.services.loadHelpPendencyHistory.load(surgical_pendency_id)
  }

  async loadSurgicalOrderNoticeCard(
    surgical_order_id: number
  ): Promise<LoadSurgicalOrderNoticeCard.Model> {
    return this.services.loadSurgicalOrderNoticeCard.load(surgical_order_id)
  }

  async createHelpPendencyMessage(
    params: CreateHelpPendencyMessage.Params
  ): Promise<CreateHelpPendencyMessage.Model> {
    return this.services.createHelpPendencyMessage.create(params)
  }

  async finishHelpPendency(
    params: FinishHelpPendency.Params
  ): Promise<FinishHelpPendency.Model> {
    return this.services.finishHelpPendency.answer(params)
  }

  async addSurgicalPendency(
    params: AddSurgicalPendency.Params
  ): Promise<AddSurgicalPendency.Model> {
    return this.services.addSurgicalPendency.add(params)
  }

  async finishSurgicalPendency(
    params: FinishSurgicalPendency.Params
  ): Promise<FinishSurgicalPendency.Model> {
    return this.services.finishSurgicalPendency.answer(params)
  }

  async loadAuthorizedCommunicationHistory(
    surgical_pendency_id: number
  ): Promise<LoadAuthorizedCommunicationHistory.Model> {
    return this.services.loadAuthorizedCommunicationHistory.load(
      surgical_pendency_id
    )
  }

  async createAuthorizedCommunicationMessage(
    params: CreateAuthorizedCommunicationMessage.Params
  ): Promise<CreateAuthorizedCommunicationMessage.Model> {
    return this.services.createAuthorizedCommunicationMessage.create(params)
  }

  async finishAuthorizedCommunication(
    params: FinishAuthorizedCommunication.Params
  ): Promise<void> {
    return this.services.finishAuthorizedCommunication.answer(params)
  }

  async answerDoctorCommunication(
    params: AnswerDoctorCommunication.Params
  ): Promise<AnswerDoctorCommunication.Model> {
    return this.services.answerDoctorCommunication.answer(params)
  }
}

export default SurgicalPendencyService
